/**
 * Advanced Filters (Professional/Advanced) — FE request contract.
 *
 * Field names are snake_case to match the backend request bodies verbatim, so
 * an `AdvancedPostFilters` object drops straight into a POST body under
 * `advanced`. For GET analytics/influencers/brand-reviews surfaces, use
 * `advancedPostToQuery` / `advancedReviewToQuery` to produce the `adv_*` query
 * params the backend query-dependencies expect — flat fields as scalars/CSV,
 * operator text fields (`TextMatch`) as a JSON-encoded param.
 */

export type TextOp =
	| 'contains'
	| 'not_contains'
	| 'is'
	| 'is_not'
	| 'starts_with'
	| 'ends_with';

export interface TextClause {
	op: TextOp;
	value: string;
}

/** One or more operator clauses joined by AND / OR (the "Add Another" list). */
export interface TextMatch {
	join: 'and' | 'or';
	clauses: TextClause[];
}

export interface AdvancedPostFilters {
	// numeric ranges
	followers_min?: number;
	followers_max?: number;
	reach_min?: number;
	reach_max?: number;
	interactions_min?: number;
	interactions_max?: number;
	hashtags_count_min?: number;
	hashtags_count_max?: number;
	// flat set / bool
	countries?: string[];
	media_types?: string[];
	author_verified?: boolean;
	keywords?: string;
	// operator text matches
	influencer_name?: TextMatch;
	influencer_user?: TextMatch;
	full_text?: TextMatch;
	title?: TextMatch;
	content_match?: TextMatch;
	url?: TextMatch;
	context?: TextMatch;
	hashtags_match?: TextMatch;
}

export interface AdvancedReviewFilters {
	// Whole-star buckets (5/4/3/2/1) — the "Review Stars" checkbox column.
	ratings?: number[];
	rating_min?: number;
	rating_max?: number;
	date_from?: string; // YYYY-MM-DD
	date_to?: string; // YYYY-MM-DD
	author_verified?: boolean;
	// Free-text search over the review title + body (case-insensitive).
	keyword?: string;
	// Case-insensitive substring match on the reviewer's display name.
	author?: string;
}

const TEXT_MATCH_KEYS: (keyof AdvancedPostFilters)[] = [
	'influencer_name',
	'influencer_user',
	'full_text',
	'title',
	'content_match',
	'url',
	'context',
	'hashtags_match'
];

/** True when the match carries at least one non-empty clause. */
export function textMatchHasValues(tm: TextMatch | null | undefined): boolean {
	return !!tm && tm.clauses.some(c => !!c.value && c.value.trim().length > 0);
}

/** True when at least one field carries a value. */
export function hasAdvancedValues(
	adv: AdvancedPostFilters | AdvancedReviewFilters | null | undefined
): boolean {
	if (!adv) return false;
	return Object.entries(adv).some(([key, v]) => {
		if (v === undefined || v === null) return false;
		if (Array.isArray(v)) return v.length > 0;
		if (TEXT_MATCH_KEYS.includes(key as keyof AdvancedPostFilters)) {
			return textMatchHasValues(v as TextMatch);
		}
		return true;
	});
}

/** `adv_*` query params for the GET post surfaces (analytics/influencers). */
export function advancedPostToQuery(
	adv: AdvancedPostFilters | null | undefined
): Record<string, string> {
	const q: Record<string, string> = {};
	if (!adv) return q;

	const num = (key: string, v: number | undefined) => {
		if (v !== undefined && v !== null && !Number.isNaN(v)) {
			q[key] = String(v);
		}
	};
	num('adv_followers_min', adv.followers_min);
	num('adv_followers_max', adv.followers_max);
	num('adv_reach_min', adv.reach_min);
	num('adv_reach_max', adv.reach_max);
	num('adv_interactions_min', adv.interactions_min);
	num('adv_interactions_max', adv.interactions_max);
	num('adv_hashtags_count_min', adv.hashtags_count_min);
	num('adv_hashtags_count_max', adv.hashtags_count_max);

	if (adv.countries?.length) q['adv_countries'] = adv.countries.join(',');
	if (adv.media_types?.length)
		q['adv_media_types'] = adv.media_types.join(',');
	if (adv.author_verified !== undefined)
		q['adv_author_verified'] = String(adv.author_verified);
	if (adv.keywords) q['adv_keywords'] = adv.keywords;

	const tm = (key: string, field: keyof AdvancedPostFilters) => {
		const value = adv[field] as TextMatch | undefined;
		if (textMatchHasValues(value)) {
			q[key] = JSON.stringify(cleanMatch(value!));
		}
	};
	tm('adv_influencer_name', 'influencer_name');
	tm('adv_influencer_user', 'influencer_user');
	tm('adv_full_text', 'full_text');
	tm('adv_title', 'title');
	tm('adv_content_match', 'content_match');
	tm('adv_url', 'url');
	tm('adv_context', 'context');
	tm('adv_hashtags_match', 'hashtags_match');
	return q;
}

/** Drops blank clauses so an all-empty match never serializes. */
function cleanMatch(tm: TextMatch): TextMatch {
	return {
		join: tm.join,
		clauses: tm.clauses.filter(c => !!c.value && c.value.trim().length > 0)
	};
}

/** `adv_*` query params for the brand reviews GET list. */
export function advancedReviewToQuery(
	adv: AdvancedReviewFilters | null | undefined
): Record<string, string> {
	const q: Record<string, string> = {};
	if (!adv) return q;
	if (adv.ratings?.length) q['adv_review_stars'] = adv.ratings.join(',');
	if (adv.rating_min !== undefined)
		q['adv_rating_min'] = String(adv.rating_min);
	if (adv.rating_max !== undefined)
		q['adv_rating_max'] = String(adv.rating_max);
	if (adv.date_from) q['adv_review_from'] = adv.date_from;
	if (adv.date_to) q['adv_review_to'] = adv.date_to;
	if (adv.author_verified !== undefined)
		q['adv_review_verified'] = String(adv.author_verified);
	if (adv.keyword) q['adv_review_keyword'] = adv.keyword;
	if (adv.author) q['adv_review_author'] = adv.author;
	return q;
}
